import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { EventSeatRounded } from '@mui/icons-material';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { useTenantStore } from '../../../stores/tenant.store';
import { programEnrollmentApi } from '../../../lib/api/program-enrollment.api';

interface Option {
  id: string;
  name: string;
}

export function CapacityTab(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();

  const [programs, setPrograms] = useState<Option[]>([]);
  const [programId, setProgramId] = useState('');

  useEffect(() => {
    if (!currentTenantId) return;
    return onSnapshot(collection(db, `tenants/${currentTenantId}/programs`), (snap) => {
      setPrograms(
        snap.docs
          .filter((d) => ((d.data()['status'] as string | undefined) ?? 'active') === 'active')
          .map((d) => ({ id: d.id, name: (d.data()['name'] as string) ?? d.id })),
      );
    });
  }, [currentTenantId]);

  const reportQ = useQuery({
    queryKey: ['capacity', currentTenantId, programId],
    enabled: !!currentTenantId && !!programId,
    queryFn: () =>
      programEnrollmentApi.getCapacityReport({ tenantId: currentTenantId!, programId }),
    staleTime: 60_000,
  });

  const report = reportQ.data;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('reports.capacity.subtitle')}
        </Typography>
      </Box>

      <TextField
        select
        size="small"
        label={t('reports.capacity.selectProgram')}
        value={programId}
        onChange={(e) => setProgramId(e.target.value)}
        sx={{ minWidth: 260, maxWidth: 360 }}
      >
        {programs.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.name}
          </MenuItem>
        ))}
      </TextField>

      {!programId && (
        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
          <EventSeatRounded sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
          <Typography variant="h6">{t('reports.capacity.emptyNoSelection')}</Typography>
        </Box>
      )}

      {!!programId && reportQ.isLoading && (
        <Stack alignItems="center" py={6}>
          <CircularProgress size={32} />
        </Stack>
      )}

      {report && (
        <>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              color="primary"
              label={t('reports.capacity.activeStudents', { count: report.activeStudents })}
            />
          </Stack>

          {report.rows.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <Typography variant="body2">{t('reports.capacity.emptyNoDemand')}</Typography>
            </Box>
          ) : (
            <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('reports.capacity.colCourse')}</TableCell>
                    <TableCell align="right">{t('reports.capacity.colDemand')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.map((r) => (
                    <TableRow key={r.courseId} hover>
                      <TableCell>{r.courseName}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>
                          {r.studentCount}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Stack>
  );
}
